import { useRef, useState } from "react"
import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"
import { endOfDay, startOfDay, subDays } from "date-fns"
import { fromZonedTime } from "date-fns-tz"
import { useResizeObserver } from "usehooks-ts"

import NoiseInsightsEmptyStateImage from "src/components/EmptyState/images/noise-insights-no-events-dashboard.svg"
import UnmonitoredHomesImage from "src/components/EmptyState/images/noise-insights-no-monitored-homes-dashboard.svg"
import { NoiseInsightGraphCard } from "src/components/Reports/NoiseInsights/NoiseInsightGraphCard"
import { TimezoneSelect } from "src/components/Reports/TimezoneSelect"
import { useAppData } from "src/context/useAppData"
import { usePostDashboardNoiseInsightsViewAnalyticsClicked } from "src/data/analytics/queries/dashboardNoiseInsightsAnalyticsQueries"
import { useFetchNoiseEventsByDate } from "src/data/noiseInsights/queries/noiseInsightQueries"
import { TNoiseInsightEventsFilter } from "src/data/noiseInsights/types/noiseInsightTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomesCount } from "src/data/organizations/queries/homeQueries"
import { TTimezone } from "src/data/timezones/timezones"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { TPartialLocation } from "src/router/routeTypes"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"
import { getStoredSelectedTimezone } from "src/utils/l10n"

const MOBILE_BREAKPOINT = 500

export function DashboardNoiseInsight() {
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()

  const startDate = startOfDay(subDays(new Date(), 30))
  const endDate = endOfDay(new Date())

  const [timezone, setTimezone] = useState<TTimezone>(
    getStoredSelectedTimezone().timezone
  )

  const filters = {
    from: fromZonedTime(startDate, timezone.tzCode).toISOString(),
    to: fromZonedTime(endDate, timezone.tzCode).toISOString(),
  }

  return (
    <div>
      <Text variant="heading2" marginBottom={spacing.L}>
        {t(langKeys.dashboard_noise_insight_title)}
      </Text>
      <NoiseInsightsContent
        orgId={orgId}
        filters={filters}
        startDate={startDate}
        endDate={endDate}
        timezone={timezone}
        setTimezone={setTimezone}
      />
    </div>
  )
}

function NoiseInsightsContent({
  orgId,
  filters,
  startDate,
  endDate,
  timezone,
  setTimezone,
}: {
  orgId: string
  filters: TNoiseInsightEventsFilter
  startDate: Date
  endDate: Date
  timezone: TTimezone
  setTimezone: (timezone: TTimezone) => void
}) {
  const { t, langKeys } = useTranslate()
  const postDashboardNoiseInsightsViewAnalyticsClicked =
    usePostDashboardNoiseInsightsViewAnalyticsClicked()
  // Fetch events day-by-day/week-by-week to pass to the graph card
  const viewRef = useRef<HTMLDivElement>(null)

  const { width = 0 } = useResizeObserver({
    ref: viewRef,
    box: "border-box",
  })

  const resolution = width < MOBILE_BREAKPOINT ? "week" : "day"

  const fetchEventsByDate = useFetchNoiseEventsByDate({
    orgId,
    filters: {
      ...filters,
      resolution: resolution,
    },
  })

  const totalEvents = fetchEventsByDate.data?.data.reduce(
    (acc, item) => acc + item.aggregation,
    0
  )
  const fetchUnmonitoredHomesCount = useFetchHomesCount({
    orgId,
    filter: { offline_sensors: "all" },
  })

  const { numberOfHomes } = useAppData()
  const allHomesAreUnmonitored =
    fetchUnmonitoredHomesCount.data === numberOfHomes

  const unmonitoredFilter = Routes.Homes.location({
    offline_sensors: "any",
    limit: fetchUnmonitoredHomesCount.data,
  })

  const filteredNoiseInsightsLocation = Routes.NoiseInsights.location({
    startDate: startOfDay(subDays(new Date(), 180)),
    endDate: endOfDay(new Date()),
  })

  if (allHomesAreUnmonitored) {
    return (
      <DashboardNoiseInsightStateCard
        image={UnmonitoredHomesImage}
        title={t(langKeys.noise_insight_all_units_unmonitored_title)}
        subtitle={t(langKeys.noise_insight_all_units_unmonitored_subtitle)}
        linkText={t(langKeys.noise_insight_all_units_unmonitored_button)}
        route={unmonitoredFilter}
      />
    )
  }

  if (totalEvents === 0) {
    return (
      <DashboardNoiseInsightStateCard
        image={NoiseInsightsEmptyStateImage}
        title={t(langKeys.noise_insight_no_events_title, { daysInRange: 30 })}
        subtitle={t(langKeys.noise_insight_no_events_30_days)}
        linkText={t(langKeys.noise_insight_events_6_months)}
        route={filteredNoiseInsightsLocation}
      />
    )
  }

  return (
    <div ref={viewRef}>
      <NoiseInsightGraphCard
        data={fetchEventsByDate.data}
        isLoading={fetchEventsByDate.isLoading}
        startDate={startDate}
        endDate={endDate}
        info={
          <InternalLink
            onClick={() => {
              postDashboardNoiseInsightsViewAnalyticsClicked.mutate()
            }}
            to={Routes.NoiseInsights.location()}
          >
            {t(langKeys.dashboard_noise_insight_link)}
          </InternalLink>
        }
        timeZoneContent={
          <TimezoneContent>
            <TimeZoneTitle>
              <Text variant="body">{t(langKeys.timezone)}: </Text>
            </TimeZoneTitle>
            <TimezoneSelect timezone={timezone} onChange={setTimezone} />
          </TimezoneContent>
        }
      />
    </div>
  )
}

function DashboardNoiseInsightStateCard({
  image: Image,
  title,
  subtitle,
  linkText,
  route,
}: {
  image: React.FC<React.SVGProps<SVGSVGElement>>
  title: string
  subtitle: string
  linkText: string
  route: string | TPartialLocation
}) {
  return (
    <MCard border boxShadow={false}>
      <CardContent>
        <ImageBox>
          <Image />
        </ImageBox>
        <div>
          <Text variant="subtitle" color="primary">
            {title}
          </Text>
          <Description>
            <Text variant="body">{subtitle}</Text>
            <InternalLink to={route} color="secondary">
              {linkText}
            </InternalLink>
          </Description>
        </div>
      </CardContent>
    </MCard>
  )
}
const TimezoneContent = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing.S};
`
const TimeZoneTitle = styled.div`
  margin-right: ${spacing.S};
`
const ImageBox = styled.div`
  width: 64px;
  height: 64px;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.L};
  align-items: center;
`

const Description = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.XS2};
`
